import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { 
  House, 
  Folder, 
  SignOut,
  User,
  List,
  Wrench,
  Users,
  Gear,
  ClipboardText,
  Warning,
  CalendarCheck
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { libelleRole, MENU } from '../lib/libelles';

// Navigation globale uniquement
// PLA-02 — « Tableau de bord » et « Projets » pour toute personne connectée : la liste
// des projets est filtrée côté serveur par appartenance (un compte bet, consultant,
// moe_*, employe_entreprise n'avait AUCUN menu). `excludeRoles` = exclusions explicites.
const globalNavigation = [
  { name: MENU.tableauDeBord, href: '/dashboard', icon: House, testid: 'nav-tableau-de-bord' },
  // Fluidité B2 (P1#10) — un seul mot : « Chantiers » (page, bouton « Nouveau chantier »)
  { name: MENU.chantiers, href: '/chantiers', icon: Folder, testid: 'nav-chantiers' },
  // Fluidité B2 (P3#22, P1#14) — « Mes actions » (souvent vide, sans menu de gauche)
  // devient « Ma semaine » : ce que montre le tableau de bord, plus les actions assignées.
  { name: MENU.maSemaine, href: '/ma-semaine', icon: CalendarCheck, excludeRoles: ['client'], testid: 'nav-ma-semaine' },
  { name: MENU.appelsOffres, href: '/consultations', icon: ClipboardText, roles: ['entreprise'], testid: "nav-appels-d'offres" }, // A6 — vocabulaire entreprise
  { name: MENU.entreprises, href: '/entreprises', icon: Wrench, roles: ['admin', 'architect', 'collaborateur'], testid: 'nav-entreprises' },
  // RP84-85 — écran unique « Équipe & accès » (remplace Équipe MOE et Clients, qui
  // redirigent ici). Le collaborateur consulte (onglet Clients compris) ; seuls
  // l'admin (et l'associé pour inviter) agissent sur les comptes.
  { name: MENU.equipeAcces, href: '/equipe-acces', icon: Users, roles: ['admin', 'architect', 'collaborateur'], testid: 'nav-équipe-&-accès' },
];

// RP03 / D37 — La section admin « Prospection » (page BOAMP, placeholder sans
// backend) a été retirée du menu.

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [modePrep, setModePrep] = useState(false);
  const [emailsPending, setEmailsPending] = useState(0);

  useEffect(() => {
    if (user?.role !== 'admin') return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API_URL}/api/admin/mode-preparation`, { withCredentials: true })
      .then(({ data }) => { setModePrep(data.mode_preparation); setEmailsPending(data.emails_pending || 0); })
      .catch(() => {});
  }, [user?.role, location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/dashboard';
    if (path === '/ma-semaine') return location.pathname === '/ma-semaine' || location.pathname === '/mes-taches';
    return location.pathname.startsWith(path);
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-[#E0E0DB]">
        <Link to="/dashboard" className="flex items-center gap-3">
          <img 
            src="/images/logo-mini.png" 
            alt="Kroquis" 
            className="w-10 h-10 object-contain"
          />
          <span className="text-xl font-semibold tracking-tight text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Kroquis
          </span>
        </Link>
      </div>

      {/* Navigation Globale */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className="px-3 mb-2">
          <span className="overline">Navigation</span>
        </div>
        {globalNavigation.filter(item => (!item.roles || item.roles.includes(user?.role)) && !(item.excludeRoles || []).includes(user?.role)).map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`sidebar-item ${active ? 'active' : ''}`}
              data-testid={item.testid}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Icon size={20} weight={active ? 'fill' : 'regular'} />
              <span className="text-sm">{item.name}</span>
            </Link>
          );
        })}
        
        {user?.role === 'amo' && (
          <>
            <div className="px-3 mt-6 mb-2">
              <span className="overline">Mon compte</span>
            </div>
            <Link
              to="/profil"
              className={`sidebar-item ${isActive('/profil') ? 'active' : ''}`}
              data-testid="nav-profil-amo"
              onClick={() => setMobileMenuOpen(false)}
            >
              <User size={20} weight={isActive('/profil') ? 'fill' : 'regular'} />
              <span className="text-sm">Mon profil</span>
            </Link>
          </>
        )}
        
        {/* Fluidité B2 (P1#26) — « Administration › Paramètres » : administrateur seulement.
            Pour les autres, c'était un doublon de « Mon compte » (en bas du menu). */}
        {isAdmin && (
          <>
            <div className="px-3 mt-6 mb-2">
              <span className="overline">{MENU.administration}</span>
            </div>
            <Link
              to="/settings"
              className={`sidebar-item ${isActive('/settings') ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <Gear size={20} weight={isActive('/settings') ? 'fill' : 'regular'} />
              <span className="text-sm">{MENU.parametres}</span>
            </Link>
          </>
        )}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-[#E0E0DB] bg-[#FAFAF8]">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-[#C75B39] text-white flex items-center justify-center text-sm font-medium">
            {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#2C2C2C] truncate">{user?.name}</p>
            <p className="text-xs text-[#6B6B6B]">{libelleRole(user?.role)}</p>
          </div>
        </div>
        {/* RP07 — Mon compte (tous les rôles) */}
        <Link
          to="/mon-compte"
          className="w-full flex items-center gap-2 px-3 py-2 mb-2 border border-[#E0E0DB] bg-white hover:bg-[#F5F5F3] text-sm text-[#2C2C2C]"
          onClick={() => setMobileMenuOpen(false)}
          data-testid="nav-mon-compte"
        >
          <User size={16} />
          Mon compte
        </Link>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 rounded-none border-[#E0E0DB] hover:bg-[#F5F5F3] text-sm"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <SignOut size={16} />
          Déconnexion
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="main-layout">
      {/* Bandeau mode préparation */}
      {modePrep && user?.role === 'admin' && (
        <div
          className="sticky top-0 z-[9999] bg-[#C4773B] text-white text-center py-1.5 px-4 text-xs font-semibold flex items-center justify-center gap-2"
          data-testid="mode-prep-banner-header"
        >
          <Warning size={14} weight="fill" />
          Envois d'e-mails en pause — {emailsPending} e-mail{emailsPending > 1 ? 's' : ''} retenu{emailsPending > 1 ? 's' : ''}.
          <Link to="/equipe-acces" className="underline ml-2 hover:text-white/80">Équipe &amp; accès →</Link>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-60 lg:fixed lg:inset-y-0 bg-white border-r border-[#E0E0DB]">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-white border-b border-[#E0E0DB] z-50 flex items-center justify-between px-4">
        <Link to="/dashboard" className="flex items-center gap-2">
          <img 
            src="/images/logo-mini.png" 
            alt="Kroquis" 
            className="w-8 h-8 object-contain"
          />
          <span className="text-lg font-semibold text-[#2C2C2C]" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Kroquis
          </span>
        </Link>

        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden">
              <List size={24} />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-60 p-0">
            <SidebarContent />
          </SheetContent>
        </Sheet>
      </header>

      {/* Main Content */}
      <main className="lg:pl-60 pt-14 lg:pt-0 min-h-screen">
        {children}
      </main>
    </div>
  );
}
